import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, Plus, Trash2, Save, X, Languages, Search, Sparkles } from 'lucide-react';
import { useT } from '../context/LanguageContext';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirm';

import { API_BASE as BASE } from '../lib/apiBase';

// Standaard set van keys waarvan we de gebruiker willen laten vertalen (uitbreidbaar)
const KEY_GROUPS = [
  { title: 'Navigatie', prefix: 'nav.' },
  { title: 'Algemeen', prefix: 'common.' },
  { title: 'Dashboard', prefix: 'dash.' },
  { title: 'Instellingen', prefix: 'set.' },
  { title: 'Voertuig / APK', prefix: 'apk.' },
];

const COMMON_FLAGS = ['🇳🇱','🇬🇧','🇩🇪','🇫🇷','🇪🇸','🇮🇹','🇵🇱','🇵🇹','🇹🇷','🇸🇦','🇨🇳','🇯🇵','🇺🇦','🇧🇪','🇱🇺','🇨🇭'];

export default function TalenManager() {
  const { t, languages, refreshLanguages, lang, setLang } = useT();
  const [editing, setEditing] = useState(null); // {code, name, flag}
  const [adding, setAdding] = useState(false);

  const handleDelete = async (code) => {
    if (!(await confirmDialog(`Taal "${code}" en alle vertalingen verwijderen?`))) return;
    const tok = localStorage.getItem('monteurcentraal_token');
    axios.delete(`${BASE}/api/i18n/languages/${code}`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(() => { toast.success(t('Taal verwijderd')); if (lang === code) setLang('nl'); refreshLanguages(); })
      .catch(e => toast.error(t('Verwijderen mislukt'), { description: e.response?.data?.detail || e.message }));
  };

  return (
    <div data-testid="talen-manager">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 className="cs-card-title" style={{ margin: 0 }}><Languages size={16} /> {t('Talen beheer')}</h3>
        <button data-testid="btn-add-language" className="cs-btn primary" onClick={() => setAdding(true)}><Plus size={14} /> {t('Taal toevoegen')}</button>
      </div>
      <p style={{ color: 'var(--cs-text-muted)', fontSize: 12.5, marginTop: 0 }}>
        {t('Voeg zoveel talen toe als je wilt (Duits, Frans, Spaans, Pools, ...) en vertaal de UI zelf. Wijzigingen zijn direct zichtbaar zonder herladen.')}
      </p>

      <table className="cs-table" data-testid="table-languages">
        <thead><tr><th>{t('Vlag')}</th><th>{t('Code')}</th><th>{t('Naam')}</th><th>{t('Type')}</th><th></th></tr></thead>
        <tbody>
          {languages.map(l => (
            <tr key={l.code} data-testid={`row-lang-${l.code}`}>
              <td style={{ fontSize: 22 }}>{l.flag}</td>
              <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{l.code}</td>
              <td>{l.name} {lang === l.code && <span className="cs-badge blue" style={{ marginLeft: 6 }}>{t('Actief')}</span>}</td>
              <td>{l.custom ? <span className="cs-badge amber">{t('Custom')}</span> : <span className="cs-badge gray">{t('Ingebouwd')}</span>}</td>
              <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                <button data-testid={`btn-translate-${l.code}`} className="cs-btn ghost" onClick={() => setEditing(l)}><Languages size={12} /> {t('Vertalingen')}</button>
                {l.custom && <button data-testid={`btn-delete-lang-${l.code}`} className="cs-btn ghost danger" onClick={() => handleDelete(l.code)}><Trash2 size={12} /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {adding && <AddLanguageModal onClose={() => setAdding(false)} onSaved={() => { refreshLanguages(); setAdding(false); }} />}
      {editing && <TranslationEditor language={editing} onClose={() => setEditing(null)} onSaved={() => { refreshLanguages(); }} />}
    </div>
  );
}

function AddLanguageModal({ onClose, onSaved }) {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [flag, setFlag] = useState('🇩🇪');
  const save = () => {
    if (!code || code.length < 2) { toast.error(t('Geef een geldige taalcode (bv. de, fr, es)')); return; }
    const tok = localStorage.getItem('monteurcentraal_token');
    axios.post(`${BASE}/api/i18n/languages`, { code: code.toLowerCase(), name, flag },
      { headers: { Authorization: `Bearer ${tok}` } })
      .then(() => { toast.success(`Taal "${name || code}" toegevoegd`); onSaved(); })
      .catch(e => toast.error(t('Toevoegen mislukt'), { description: e.response?.data?.detail || e.message }));
  };
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid="modal-add-lang">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{t('Nieuwe taal toevoegen', 'Nieuwe taal toevoegen')}</h3>
          <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div>
            <label className="cs-section-title">{t('Taalcode (ISO 639-1)')}</label>
            <input data-testid="input-lang-code" className="cs-input" placeholder={t('bv. de, fr, es, pl')} value={code} onChange={e => setCode(e.target.value.toLowerCase().slice(0, 5))} style={{ fontFamily: 'ui-monospace, monospace', textTransform: 'lowercase' }} />
          </div>
          <div>
            <label className="cs-section-title">{t('Naam (in eigen taal)')}</label>
            <input data-testid="input-lang-name" className="cs-input" placeholder={t('bv. Deutsch, Français, Español')} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label className="cs-section-title">{t('Vlag emoji')}</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {COMMON_FLAGS.map(f => (
                <button key={f} type="button" className="cs-btn ghost" style={{ fontSize: 18, padding: '4px 10px', background: flag === f ? 'var(--cs-accent-soft)' : 'transparent' }} onClick={() => setFlag(f)}>{f}</button>
              ))}
            </div>
            <input className="cs-input" value={flag} onChange={e => setFlag(e.target.value)} style={{ fontSize: 18, width: 80 }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="cs-btn" onClick={onClose}>{t('Annuleren')}</button>
          <button data-testid="btn-save-lang" className="cs-btn primary" onClick={save}><Save size={14} /> {t('Taal aanmaken')}</button>
        </div>
      </div>
    </div>
  );
}

function TranslationEditor({ language, onClose, onSaved }) {
  const { t } = useT();
  const [entries, setEntries] = useState({});
  const [nlBase, setNlBase] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState('');
  const [dirty, setDirty] = useState({});
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem('monteurcentraal_token');
    Promise.all([
      axios.get(`${BASE}/api/i18n/translations/${language.code}`, { headers: { Authorization: `Bearer ${tok}` } }),
      axios.get(`${BASE}/api/i18n/translations/nl`, { headers: { Authorization: `Bearer ${tok}` } }),
    ])
      .then(([cur, ref]) => {
        const refEntries = ref.data?.entries || {};
        const allKeys = Object.keys(refEntries);
        const curEntries = cur.data?.entries || {};
        const merged = {};
        allKeys.forEach(k => { merged[k] = curEntries[k] || ''; });
        setEntries(merged);
        setNlBase(refEntries);
        setLoaded(true);
      })
      .catch(e => { toast.error(t('Laden mislukt'), { description: e.message }); setLoaded(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language.code]);

  const handleSave = () => {
    const tok = localStorage.getItem('monteurcentraal_token');
    const toSave = {};
    Object.entries(entries).forEach(([k, v]) => { if (v && v.trim()) toSave[k] = v; });
    axios.put(`${BASE}/api/i18n/translations/${language.code}`, { entries: toSave }, { headers: { Authorization: `Bearer ${tok}` } })
      .then(() => { toast.success(`${Object.keys(toSave).length} vertalingen opgeslagen voor ${language.name}`); setDirty({}); onSaved(); })
      .catch(e => toast.error(t('Opslaan mislukt'), { description: e.response?.data?.detail || e.message }));
  };

  const handleAutoTranslate = async () => {
    // Vertaal alleen lege keys
    const emptyKeys = Object.keys(entries).filter(k => !entries[k] || !entries[k].trim());
    if (emptyKeys.length === 0) { toast.info(t('Alle keys zijn al vertaald')); return; }
    if (!(await confirmDialog(`Vertaal ${emptyKeys.length} lege keys automatisch naar ${language.name} via AI? Bestaande vertalingen blijven.`))) return;
    const tok = localStorage.getItem('monteurcentraal_token');
    const sourcePayload = {};
    emptyKeys.forEach(k => { sourcePayload[k] = nlBase[k] || k; });
    setTranslating(true);
    axios.post(`${BASE}/api/i18n/auto-translate`, { targetLang: language.code, entries: sourcePayload },
      { headers: { Authorization: `Bearer ${tok}` }, timeout: 60000 })
      .then(r => {
        const translated = r.data?.translated || {};
        const merged = { ...entries };
        const newDirty = { ...dirty };
        Object.entries(translated).forEach(([k, v]) => { merged[k] = v; newDirty[k] = true; });
        setEntries(merged);
        setDirty(newDirty);
        toast.success(`AI vertaalde ${r.data.count} keys`, { description: 'Bekijk en klik "Opslaan" om te bewaren.' });
      })
      .catch(e => toast.error(t('Auto-vertalen mislukt'), { description: e.response?.data?.detail || e.message }))
      .finally(() => setTranslating(false));
  };

  const keys = Object.keys(entries).filter(k => !filter || k.toLowerCase().includes(filter.toLowerCase()) || (entries[k] || '').toLowerCase().includes(filter.toLowerCase()));
  const groupedKeys = KEY_GROUPS.map(g => ({ ...g, keys: keys.filter(k => k.startsWith(g.prefix)) }));
  const other = keys.filter(k => !KEY_GROUPS.some(g => k.startsWith(g.prefix)));
  const emptyCount = Object.keys(entries).filter(k => !entries[k]).length;

  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={e => e.stopPropagation()} data-testid={`modal-translate-${language.code}`} style={{ width: 760, maxHeight: '90vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{language.flag} {language.name} <span style={{ fontSize: 12, color: 'var(--cs-text-muted)', fontWeight: 400 }}>({Object.keys(entries).filter(k => entries[k]).length}/{Object.keys(entries).length} vertaald)</span></h3>
          <div style={{ display: 'flex', gap: 6 }}>
            {language.code !== 'nl' && emptyCount > 0 && (
              <button data-testid="btn-auto-translate" className="cs-btn" onClick={handleAutoTranslate} disabled={translating} title={t('Vul lege keys automatisch via AI')}>
                <Sparkles size={14} /> {translating ? `${t('Vertalen')} (${emptyCount})...` : `${t('AI vertaal')} ${emptyCount} ${t('leeg')}`}
              </button>
            )}
            <button className="cs-btn ghost" onClick={onClose}><X size={14} /></button>
          </div>
        </div>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--cs-text-muted)' }} />
          <input data-testid="filter-keys" className="cs-input" style={{ paddingLeft: 32 }} placeholder={t('Zoek in keys of vertalingen...')} value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        {!loaded ? <div className="cs-empty">{t('Vertalingen laden...')}</div> : (
          <div style={{ maxHeight: 'calc(90vh - 240px)', overflowY: 'auto', paddingRight: 6 }}>
            {[...groupedKeys, { title: 'Overig', keys: other }].filter(g => g.keys.length > 0).map(g => (
              <div key={g.title} style={{ marginBottom: 18 }}>
                <h4 style={{ margin: '6px 0', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--cs-accent)' }}>{g.title} ({g.keys.length})</h4>
                {g.keys.map(k => (
                  <div key={k} style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: 8, marginBottom: 4, alignItems: 'center' }}>
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: 'var(--cs-text-muted)' }}>{k}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)', fontStyle: 'italic' }}>{nlBase[k]}</div>
                    <input
                      data-testid={`tr-${k}`}
                      className="cs-input"
                      style={{ padding: '6px 8px', fontSize: 13, background: dirty[k] ? '#fef3c7' : '#fff' }}
                      value={entries[k] || ''}
                      placeholder={`(leeg = NL fallback)`}
                      onChange={e => { setEntries({ ...entries, [k]: e.target.value }); setDirty({ ...dirty, [k]: true }); }}
                    />
                  </div>
                ))}
              </div>
            ))}
            {keys.length === 0 && <div className="cs-empty">{t('Geen keys gevonden voor', 'Geen keys gevonden voor')} &quot;{filter}&quot;.</div>}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 10, borderTop: '1px solid var(--cs-border)' }}>
          <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{t('Tip: laat een veld leeg om de Nederlandse fallback te gebruiken. Geel = nog niet opgeslagen.')}</div>
          <button data-testid="btn-save-translations" className="cs-btn primary" onClick={handleSave}><Save size={14} /> {t('Opslaan')}</button>
        </div>
      </div>
    </div>
  );
}
